import { Input } from "react-aria-components";
import { ChangeEvent, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { MDSEditableTitleProps } from "@/mds/components/inputs/editable-title/editable-title.types";
import { Theme } from "@emotion/react";
import { MDSTextVariant } from "@/mds/components/text";

export const MDSEditableTitle = ({
  placeholder,
  value,
  onChange,
  ...props
}: MDSEditableTitleProps) => {
  const { slot, ...restProps } = props as MDSEditableTitleProps & { slot?: string };
  const [uncontrolledValue, setUncontrolledValue] = useState<string>("");
  const isControlled = value !== undefined;
  const currentValue = (isControlled ? (value as string) : uncontrolledValue) ?? "";

  const textForMeasurement = currentValue.length > 0 ? currentValue : placeholder;

  const sizerRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputWidthPx, setInputWidthPx] = useState<number>();

  const recomputeWidth = useCallback(() => {
    const sizer = sizerRef.current;
    if (!sizer) return;
    // Add a small buffer to avoid caret clipping
    const buffer = 2;
    const measured = Math.ceil(sizer.scrollWidth) + buffer;
    if (Number.isFinite(measured) && measured > 0) {
      setInputWidthPx(measured);
    }
  }, []);

  useLayoutEffect(() => {
    recomputeWidth();
  }, [textForMeasurement, props.variant, recomputeWidth]);

  const handleChange: MDSEditableTitleProps["onChange"] = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setUncontrolledValue(e.currentTarget.value);
      onChange?.(e);
    },
    [isControlled, onChange]
  );

  const forwardedProps = useMemo(
    () => ({
      ...restProps,
      placeholder,
    }),
    [restProps, placeholder]
  );

  return (
    <Wrapper slot={slot}>
      <HiddenSizer ref={sizerRef} variant={props.variant} aria-hidden>
        {textForMeasurement || ""}
      </HiddenSizer>
      <StyledInput
        ref={inputRef}
        value={value}
        onChange={handleChange}
        {...forwardedProps}
        style={{ width: inputWidthPx ? `${inputWidthPx}px` : undefined }}
      />
    </Wrapper>
  );
};

const StyledInput = styled(Input)<{ variant: MDSTextVariant }>`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme, variant }) => getFontSize({ theme, variant })};
  line-height: ${({ theme, variant }) => getLineHeight({ theme, variant })};
  font-weight: ${({ theme, variant }) => getFontWeight({ theme, variant })};
  padding-block: ${({ theme, variant }) => getPaddingBlock({ theme, variant })};

  padding-inline: ${({ theme }) => theme.spacing["0.5"]};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.background.input.inline.default};

  &[data-hovered] {
    background-color: ${({ theme }) => theme.colors.background.input.inline.hover};
  }

  &[data-focused] {
    background-color: ${({ theme }) => theme.colors.background.input.inline.focused};
  }
`;

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;

const HiddenSizer = styled.span<{ variant: MDSTextVariant }>`
  position: absolute;
  visibility: hidden;
  white-space: pre;
  pointer-events: none;
  user-select: none;

  font-size: ${({ theme, variant }) => getFontSize({ theme, variant })};
  line-height: ${({ theme, variant }) => getLineHeight({ theme, variant })};
  font-weight: ${({ theme, variant }) => getFontWeight({ theme, variant })};
  padding-block: ${({ theme, variant }) => getPaddingBlock({ theme, variant })};
  padding-inline: ${({ theme }) => theme.spacing["0.5"]};
`;

const getFontSize = ({ variant, theme }: { variant: MDSTextVariant; theme: Theme }) => {
  return theme.fontSizes[variant];
};

const getLineHeight = ({ variant, theme }: { variant: MDSTextVariant; theme: Theme }) => {
  return theme.lineHeights[variant];
};

const getFontWeight = ({ theme, variant }: { theme: Theme; variant: MDSTextVariant }) => {
  switch (variant) {
    case MDSTextVariant.HeaderL:
      return theme.fontWeights.semiBold;
    case MDSTextVariant.HeaderM:
      return theme.fontWeights.semiBold;
    case MDSTextVariant.ParagraphS:
      return theme.fontWeights.regular;
  }
};

const getPaddingBlock = ({ theme, variant }: { theme: Theme; variant: MDSTextVariant }) => {
  switch (variant) {
    case MDSTextVariant.HeaderL:
      return theme.spacing["0.5"];
    case MDSTextVariant.HeaderM:
      return theme.spacing["0.5"];
    case MDSTextVariant.ParagraphS:
      return "0";
  }
};
